// Set the minLength to 2 if the input is in Chinese/Japanese/Korean
// else set the minLength to 3
export const getInputMinLengthLimit = (value: string): number => {
    const cjkRegex = /[\u4e00-\u9fff\u3040-\u30ff\u3400-\u4dbf\uac00-\ud7af]/u;
    return cjkRegex.test(value) ? 2 : 3;
};

export const encodedMACFormat = (mac: string): boolean => {
    mac = mac.trim();
    if (mac.length === 10 && mac.substring(0, 2) === '++') {
        return true;
    }
    return false;
};

export const decodeMac = (encoded_mac: string): string => {
    const bstr: number[] = [];
    const str = encoded_mac;

    for (let z = 0; z < encoded_mac.length; z++) {
        bstr.push(str.substring(z, z + 1).charCodeAt(0));
    }

    let j = 0;
    let ii = 0;
    let idx = 2;
    const output: number[] = [];

    for (let z = 0; z < 6; z++) {
        const m = (bstr[ii] - 48) << idx;
        const n = (bstr[ii + 1] - 48) >> (6 - idx);
        output.push((m | n) & 255);

        j += 8;
        while (j >= 6) {
            j -= 6;
            ii++;
        }
        idx += 2;
        if (idx === 8) {
            idx = 2;
        }
    }

    let barcode = '';
    for (let z = 0; z < output.length; z++) {
        let newChar = output[z].toString(16);
        if (newChar.length === 1) {
            newChar = '0' + newChar;
        }
        barcode = barcode.concat(newChar);
    }
    return barcode.toUpperCase();
};

/** 計算 UPC-A / GTIN-12 的 Mod-10 檢查碼（輸入 11 碼） */
const calcUpcaCheckDigit = (upca11: string): string => {
    if (!/^\d{11}$/.test(upca11)) {
        throw new Error('calcUpcaCheckDigit: input must be exactly 11 digits');
    }

    let sum = 0;
    for (let i = 0; i < 11; i++) {
        const digit = upca11.charCodeAt(i) - 48;
        // 1-based 奇數位*3；i 從 0 起算，故 i%2===0 視為奇數位
        sum += i % 2 === 0 ? digit * 3 : digit;
    }

    const mod = sum % 10;
    return mod === 0 ? '0' : String(10 - mod);
};

/** 將 UPC-E（6/7/8 碼）展開成 UPC-A（12 碼）；僅支援 number system 0/1 */
const expandUpceToUpca = (input: string, defaultNS: '0' | '1' = '0'): string => {
    const digits = (input || '').replace(/\D+/g, '');
    if (digits.length < 6 || digits.length > 8) {
        throw new Error('UPC-E must be 6, 7, or 8 digits');
    }

    let ns: '0' | '1' = defaultNS;
    let body6 = '';
    let chkFromInput: string | undefined;

    if (digits.length === 6) {
        // 6 碼：純主體，使用預設 NS
        body6 = digits;
    } else if (digits.length === 7) {
        // 7 碼：**優先**視為「主體6 + 檢查碼1」，用預設 NS 驗證檢查碼
        const candidateBody = digits.slice(0, 6);
        const candidateChk = digits[6];
        const exp11 = upceBodyToUpca11(ns, candidateBody);
        const expectChk = calcUpcaCheckDigit(exp11);
        if (candidateChk === expectChk) {
            body6 = candidateBody;
            chkFromInput = candidateChk;
        } else if ((digits[0] === '0' || digits[0] === '1') && /^\d{6}$/.test(digits.slice(1))) {
            // 回退情境：當成「NS + 主體6」（此時輸入不含檢查碼）
            ns = digits[0] as '0' | '1';
            body6 = digits.slice(1);
        } else {
            throw new Error('Invalid 7-digit UPC-E: neither body+check nor NS+body');
        }
    } else {
        // 8 碼：NS + 主體6 + 檢查碼1（可完整驗證）
        ns = digits[0] as '0' | '1';
        if (!/^[01]$/.test(ns)) throw new Error('UPC-E number system must be 0 or 1');
        body6 = digits.slice(1, 7);
        chkFromInput = digits[7];
        const exp11 = upceBodyToUpca11(ns, body6);
        const expectChk = calcUpcaCheckDigit(exp11);
        if (chkFromInput !== expectChk) throw new Error('Bad UPC-E check digit');
    }

    const upca11 = upceBodyToUpca11(ns, body6);
    const finalChk = chkFromInput ?? calcUpcaCheckDigit(upca11);
    return upca11 + finalChk;
};

// 依 D6 展開為 UPC-A 前 11 碼：NS(1) + manufacturer(5) + product(5)
function upceBodyToUpca11(ns: '0' | '1', body6: string): string {
    if (!/^\d{6}$/.test(body6)) throw new Error('UPC-E body must be 6 digits');
    const [d1, d2, d3, d4, d5, d6] = body6.split('');

    let manufacturer: string;
    let product: string;

    switch (d6) {
        case '0':
        case '1':
        case '2':
            // NS D1 D2 D6 0000 D3 D4 D5  →  mfr: D1 D2 D6 00 ; prod: 00 D3 D4 D5
            manufacturer = `${d1}${d2}${d6}00`;
            product = `00${d3}${d4}${d5}`;
            break;
        case '3':
            // NS D1 D2 D3 00000 D4 D5  →  mfr: D1 D2 D3 00 ; prod: 000 D4 D5   ← **這裡是 3 個 0**
            manufacturer = `${d1}${d2}${d3}00`;
            product = `000${d4}${d5}`; // 修正：原碼用 4 個 0 是錯的
            break;
        case '4':
            // NS D1 D2 D3 D4 00000 D5  →  mfr: D1 D2 D3 D4 0 ; prod: 0000 D5
            manufacturer = `${d1}${d2}${d3}${d4}0`;
            product = `0000${d5}`;
            break;
        default: // '5'..'9'
            // NS D1 D2 D3 D4 D5 0000 D6  →  mfr: D1 D2 D3 D4 D5 ; prod: 0000 D6
            manufacturer = `${d1}${d2}${d3}${d4}${d5}`;
            product = `0000${d6}`;
            break;
    }

    const upca11 = `${ns}${manufacturer}${product}`;
    if (!/^\d{11}$/.test(upca11)) throw new Error('Expanded UPC-A body must be 11 digits');
    return upca11;
}

/** 建議同時支援：先展開（若指定），再補到 13 碼（若指定） */
export const formatBarcode = (autoConvertToUpca: boolean, addLeadingZero: boolean, value: string) => {
    let out = (value || '').trim();
    try {
        if (autoConvertToUpca) out = expandUpceToUpca(out); // 變 12 碼
        if (addLeadingZero && out.length < 13) out = out.padStart(13, '0'); // 轉成 EAN-13 表示
    } catch (err) {
        console.warn('Barcode Format Error', err);
    }
    return out;
};

export const normalizeEslMac = (raw: string) => {
    return encodedMACFormat(raw) ? decodeMac(raw.substring(2)) : raw;
};
